/**
 * Notification Bell Component
 * Displays notification bell icon with unread count badge and dropdown.
 */
import { useState, useRef, useEffect } from 'react';
import { Bell, Check, CheckCheck, Trash2, X, Server, AlertCircle, CreditCard, MessageSquare, Info } from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';

// Notification type icons
const notificationIcons = {
  instance_status: Server,
  instance_created: Server,
  instance_deleted: Server,
  billing_alert: CreditCard,
  support_reply: MessageSquare,
  system_alert: AlertCircle,
  warning: AlertCircle,
  error: X,
  success: Check,
  info: Info,
};

// Notification type colors
const notificationColors = {
  instance_status: 'text-blue-400',
  instance_created: 'text-emerald-400',
  instance_deleted: 'text-red-400',
  billing_alert: 'text-yellow-400',
  support_reply: 'text-cyan-400',
  system_alert: 'text-orange-400',
  warning: 'text-yellow-400',
  error: 'text-red-400',
  success: 'text-emerald-400',
  info: 'text-blue-400',
};

function NotificationItem({ notification, onMarkRead }) {
  const Icon = notificationIcons[notification.type] || Info;
  const colorClass = notificationColors[notification.type] || 'text-zinc-400';
  
  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <div 
      className={`px-4 py-3 hover:bg-zinc-800/50 transition-colors cursor-pointer ${
        !notification.read ? 'bg-emerald-500/5 border-l-2 border-emerald-500' : ''
      }`}
      onClick={() => !notification.read && onMarkRead(notification.id)}
    >
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 ${colorClass}`}>
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white truncate">{notification.title}</p>
          <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{notification.message}</p>
          <p className="text-xs text-zinc-500 mt-1">{timeAgo(notification.created_at || notification.timestamp)}</p>
        </div>
        {!notification.read && (
          <div className="w-2 h-2 bg-emerald-500 rounded-full mt-1.5 flex-shrink-0" />
        )}
      </div>
    </div>
  );
}

export default function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearAll, connected } = useNotifications();

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        data-testid="notification-bell"
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-zinc-400 hover:text-white transition-colors"
      >
        <Bell size={20} />
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span 
            data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-emerald-500 text-black text-xs font-bold rounded-full flex items-center justify-center px-1"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        {/* Connection indicator */}
        <span 
          className={`absolute bottom-0.5 right-0.5 w-2 h-2 rounded-full ${
            connected ? 'bg-emerald-500' : 'bg-zinc-500'
          }`}
          title={connected ? 'Connected' : 'Disconnected'}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div 
          data-testid="notification-dropdown"
          className="absolute right-0 top-full mt-2 w-80 max-h-[400px] bg-[#0A0A0A] border border-zinc-800 rounded-sm shadow-xl overflow-hidden z-50"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
            <h3 className="text-sm font-mono font-bold text-white">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
                  title="Mark all as read"
                >
                  <CheckCheck size={14} />
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={clearAll}
                  className="text-xs text-zinc-500 hover:text-red-400 flex items-center gap-1"
                  title="Clear all"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Notification List */}
          <div className="max-h-[320px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell size={24} className="text-zinc-600 mx-auto mb-2" />
                <p className="text-sm text-zinc-500">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkRead={markAsRead}
                />
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-zinc-800 text-center">
              <span className="text-xs text-zinc-500">
                {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
